import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  Brain,
  Target,
  TrendingUp,
  MapPin,
  DollarSign,
  Clock,
  Star,
  ArrowRight,
  Users,
  Sparkles,
  Zap,
  Award,
  BookOpen,
  Briefcase,
  ChevronRight,
  Lock,
  Building,
  CheckCircle,
  Rocket,
  BarChart3,
  Calendar,
  Download,
  Globe,
  Lightbulb,
  Shield,
  PlayCircle
} from 'lucide-react';
import { careerAPI, paymentAPI } from '../utils/api';
import authService from '../firebase/authService';
import AuthModal from '../components/AuthModal';
import LoadingSpinner from '../components/LoadingSpinner';
import InlineSpinner from '../components/InlineSpinner';
import SEO, { seoConfigs } from '../components/SEO';

const CareerInsightsPage = ({ userProfile, careerSuggestions, onPaymentSuccess, onRoadmapGenerated }) => {
  const navigate = useNavigate();
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [selectedCareer, setSelectedCareer] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRefinementQuestions, setShowRefinementQuestions] = useState(false);
  const [refinementAnswers, setRefinementAnswers] = useState({});
  const [user, setUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [streamProgress, setStreamProgress] = useState(null);
  const [roadmapProgress, setRoadmapProgress] = useState(null);

  // Derive UI-only fields from API model
  const transformCareerPaths = (paths = []) => {
    const formatInr = (n) => {
      try { return `₹${Number(n).toLocaleString('en-IN')}/yr`; } catch { return '—'; }
    };
    const mapGrowth = (g) => {
      if (g === 'high') return 'High';
      if (g === 'medium') return 'Medium';
      if (g === 'low') return 'Low';
      return '—';
    };
    const mapFlex = (remoteFriendly) => (remoteFriendly ? 'Remote-friendly' : 'Office/Hybrid');

    return paths.map((p) => {
      const jm = p.job_market || {};
      const salaryInr = jm.average_salary_inr;
      const salaryUsd = jm.average_salary_usd;
      const salaryRange = salaryInr ? (
        salaryUsd ? `${formatInr(salaryInr)} (~$${Number(salaryUsd).toLocaleString('en-US')}/yr)` : `${formatInr(salaryInr)}`
      ) : '—';

      const growthPotential = mapGrowth(jm.growth_outlook);
      const locationFlex = mapFlex(jm.remote_friendly);
      const requiredSkills = Array.isArray(jm.skills_in_demand) && jm.skills_in_demand.length > 0
        ? jm.skills_in_demand
        : (Array.isArray(p.skill_gaps) ? p.skill_gaps.map(sg => sg.skill).filter(Boolean) : []);

      return {
        ...p,
        salary_range: salaryRange,
        growth_potential: growthPotential,
        location_flexibility: locationFlex,
        required_skills: requiredSkills,
      };
    });
  };

  // Check if user came from resume upload (they would have suggestions but no refinement answers)
  const isResumeUser = suggestions.length > 0 && !localStorage.getItem('refinementAnswers');

  const loadItem = (key) => {
    try {
      const item = localStorage.getItem(key);
      return item && item !== 'undefined' ? JSON.parse(item) : null;
    } catch (error) {
      console.warn(`Could not parse ${key} from localStorage:`, error);
      return null;
    }
  };

  const fetchSuggestions = async (retryCount = 0) => {
    setLoading(true);
    const maxRetries = 2;

    try {
      let currentSuggestions = careerSuggestions;
      let currentProfile = userProfile;

      console.log('CareerInsightsPage loading:', {
        propsProfile: !!userProfile,
        propsSuggestions: !!careerSuggestions
      });

      // If props are not available, try localStorage as a fallback
      if (!currentSuggestions) {
        const suggestionsFromStorage = loadItem('careerSuggestions');
        console.log('Loaded suggestions from storage:', !!suggestionsFromStorage);
        if (suggestionsFromStorage && suggestionsFromStorage.career_paths) {
          currentSuggestions = suggestionsFromStorage;
        }
      }

      if (!currentProfile) {
        const profileFromStorage = loadItem('userProfile');
        console.log('Loaded profile from storage:', !!profileFromStorage);
        if (profileFromStorage) {
          currentProfile = profileFromStorage;
        }
      }

      console.log('Final data check:', {
        hasProfile: !!currentProfile,
        hasSuggestions: !!currentSuggestions
      });

      // If we still don't have a profile, provide a fallback instead of forcing them away.
      if (!currentProfile || Object.keys(currentProfile).length === 0) {
        console.log('No profile found, redirecting to questionnaire generator');
        toast('We need a bit more info to customize your roadmap!', { icon: 'ℹ️' });
        navigate('/build-roadmap', { state: { returnTo: '/insights' } });
        return;
      }

      // Check if we should force refresh suggestions (when coming from navbar)
      const shouldForceRefresh = !currentSuggestions ||
        (currentSuggestions && currentSuggestions.career_paths &&
          currentSuggestions.career_paths.length === 3 &&
          currentSuggestions.career_paths[0].title === "Softwareee Developer" &&
          currentSuggestions.career_paths[1].title === "Data Analyst" &&
          currentSuggestions.career_paths[2].title === "Product Manager");

      // If we have a profile but no suggestions, or if we detect fallback data, fetch them.
      if (!currentSuggestions || shouldForceRefresh) {
        console.log('Fetching new suggestions for profile:', currentProfile);
        if (shouldForceRefresh) {
          console.log('Detected fallback data, forcing fresh API call');
          // Clear any cached suggestions
          localStorage.removeItem('careerSuggestions');
        }

        toast.loading('Analyzing your profile and generating personalized career suggestions...');

        // Start SSE stream for progress (best-effort; ignores errors silently)
        try {
          const sse = new EventSource(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/stream-suggest-careers`);
          sse.onmessage = (e) => {
            try { setStreamProgress(JSON.parse(e.data)); } catch { }
          };
          sse.onerror = () => { sse.close(); };
          // Auto-close after a while
          setTimeout(() => sse.close(), 8000);
        } catch { }

        try {
          const fetchedSuggestions = await careerAPI.suggestCareers(currentProfile);
          currentSuggestions = fetchedSuggestions;
          localStorage.setItem('careerSuggestions', JSON.stringify(fetchedSuggestions));
        } catch (apiError) {
          console.error('API call failed, trying fallback:', apiError);

          // If API fails, show a helpful message but don't fail completely
          toast.error('AI service temporarily unavailable. Using enhanced fallback suggestions.');
          console.log('Using fallback suggestions due to API failure');
          // The fallback will be handled by the API service itself
          throw new Error('Service temporarily unavailable. Please try again in a few minutes.');
        }
      }

      if (currentSuggestions && currentSuggestions.career_paths && currentSuggestions.career_paths.length > 0) {
        console.log('Setting suggestions:', currentSuggestions.career_paths.length, 'paths found');
        const transformed = transformCareerPaths(currentSuggestions.career_paths);
        setSuggestions(transformed);
        setSelectedCareer(transformed[0]);

        // Success message
        if (retryCount === 0) {
          toast.success(`Found ${currentSuggestions.career_paths.length} personalized career matches for you!`);
        }
      } else {
        console.log('No career paths found in suggestions:', currentSuggestions);
        throw new Error("Unable to generate career suggestions. Please try again.");
      }

    } catch (err) {
      console.error('Error in fetchSuggestions:', err);

      // Enhanced error handling with retry logic
      if (retryCount < maxRetries && err.message.includes('temporarily')) {
        console.log(`Retrying... attempt ${retryCount + 1}`);
        setTimeout(() => fetchSuggestions(retryCount + 1), 2000);
        return;
      }

      setError(err.message || 'Failed to get career suggestions. Please try again.');
      toast.error(err.message || 'Could not fetch career suggestions.');

      // Offer recovery options
      if (err.message.includes('profile')) {
        toast('Try refreshing your profile information.', { icon: 'ℹ️' });
      } else if (err.message.includes('service')) {
        toast('Our AI service is temporarily busy. Please try again in a few minutes.', { icon: 'ℹ️' });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSuggestions();
  }, [navigate, userProfile, careerSuggestions]);

  const refinementQuestions = [
    {
      id: 'timeline',
      question: 'When do you want to make this career transition?',
      options: ['Within 6 months', '6-12 months', '1-2 years', '2+ years'],
    },
    {
      id: 'commitment',
      question: 'How many hours per week can you dedicate to learning?',
      options: ['5-10 hours', '10-20 hours', '20-30 hours', '30+ hours'],
    },
    {
      id: 'learning_style',
      question: 'What is your preferred learning style?',
      options: ['Hands-on projects', 'Structured courses', 'Self-directed reading', 'Mentorship & coaching'],
    },
    {
      id: 'budget',
      question: 'What is your budget for learning resources?',
      options: ['Free resources only', 'Up to $150', '$150 - $600', '$600+'],
    },
  ];

  const handleRefinementAnswer = (questionId, answer) => {
    setRefinementAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }));
  };

  const handlePaymentAndUnlock = async () => {
    if (!selectedCareer) {
      toast.error("Please select a career path first.");
      return;
    }
    let profileData = loadItem('userProfile');
    if (!profileData || Object.keys(profileData).length === 0) {
      toast('We need a bit more info to customize your roadmap!', { icon: 'ℹ️' });
      navigate('/build-roadmap', { state: { returnTo: '/insights' } });
      return;
    }

    setIsPaymentLoading(true);
    try {
      toast.loading('Processing your request...');

      // For this flow, we'll use a test payment endpoint
      const paymentResponse = await paymentAPI.createTestPayment(
        profileData?.name || 'Test User',
        'test@example.com' // Using a dummy email
      );

      toast.success('Payment successful! Generating your personalized roadmap...');

      // Start roadmap SSE progress (best-effort)
      try {
        const sseRoadmap = new EventSource(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/stream-roadmap`);
        sseRoadmap.onmessage = (e) => {
          try { setRoadmapProgress(JSON.parse(e.data)); } catch { }
        };
        sseRoadmap.onerror = () => { sseRoadmap.close(); };
        setTimeout(() => sseRoadmap.close(), 12000);
      } catch { }

      // Generate roadmap with enhanced error handling
      const roadmapData = await careerAPI.generateRoadmap(
        profileData,
        selectedCareer.title,
        paymentResponse.order_id
      );

      // Validate roadmap data
      if (!roadmapData || !roadmapData.weeks || roadmapData.weeks.length === 0) {
        throw new Error('Generated roadmap appears to be incomplete. Please try again.');
      }

      // Store roadmap data in localStorage for immediate access
      localStorage.setItem('roadmapData', JSON.stringify(roadmapData));

      // Store roadmap ID for progress tracking
      if (roadmapData.roadmap_id) {
        localStorage.setItem('selectedRoadmapId', roadmapData.roadmap_id);
      }

      if (onRoadmapGenerated) {
        onRoadmapGenerated(roadmapData);
      }

      toast.success(`Roadmap generated successfully! ${roadmapData.weeks.length} weeks of personalized learning content ready.`);
      navigate('/roadmap');

    } catch (error) {
      console.error('Payment or Roadmap generation failed:', error);

      // Enhanced error handling with specific messages
      if (error.message.includes('payment')) {
        toast.error('Payment processing failed. Please check your payment method and try again.');
      } else if (error.message.includes('roadmap')) {
        toast.error('Roadmap generation failed. Our team has been notified and will investigate.');
      } else if (error.message.includes('network') || error.message.includes('timeout')) {
        toast.error('Connection issue. Please check your internet and try again.');
      } else {
        toast.error(error.message || 'An unexpected error occurred. Please try again.');
      }

      // Log error for debugging
      console.error('Detailed error:', {
        message: error.message,
        stack: error.stack,
        selectedCareer: selectedCareer?.title,
        profileData: !!profileData
      });
    } finally {
      setIsPaymentLoading(false);
    }
  };

  const handleStartRefinement = () => {
    // Store selected career and navigate to dedicated questions page
    localStorage.setItem('selectedCareer', JSON.stringify(selectedCareer));
    navigate('/questions', { state: { selectedCareer } });
  };

  const handleQuickPersonalize = () => {
    // For resume users, also redirect to full questions page for comprehensive refinement
    if (!selectedCareer) {
      toast.error("Please select a career path first.");
      return;
    }
    localStorage.setItem('selectedCareer', JSON.stringify(selectedCareer));
    navigate('/questions', { state: { selectedCareer, fromResume: true } });
  };

  const handleProceedToPayment = () => {
    // Store refinement answers for the roadmap generation
    localStorage.setItem('refinementAnswers', JSON.stringify(refinementAnswers));
    handlePaymentAndUnlock();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center text-white">
        <div className="text-center">
          <Rocket className="w-16 h-16 text-emerald-400 mx-auto mb-4 animate-bounce" />
          <h2 className="text-2xl font-bold mb-2">Analyzing Your Career Trajectory...</h2>
          <p className="text-slate-400 mb-6">Our AI is crunching the data to find your perfect fit.</p>
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center text-white">
        <div className="text-center bg-slate-800 p-8 rounded-lg max-w-md mx-auto">
          <Brain className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Unable to Generate Suggestions</h2>
          <p className="text-slate-400 mb-6">{error}</p>

          <div className="space-y-3">
            <button
              onClick={() => {
                setError(null);
                fetchSuggestions();
              }}
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 px-4 rounded-lg transition-colors"
            >
              Try Again
            </button>

            <button
              onClick={() => navigate('/build-roadmap')}
              className="w-full bg-slate-700 hover:bg-slate-600 text-white font-bold py-3 px-4 rounded-lg transition-colors"
            >
              Update Profile
            </button>

            <button
              onClick={() => {
                localStorage.clear();
                window.location.reload();
              }}
              className="w-full bg-slate-600 hover:bg-slate-500 text-white font-bold py-3 px-4 rounded-lg transition-colors"
            >
              Reset & Start Fresh
            </button>
          </div>

          <div className="mt-6 pt-4 border-t border-slate-700">
            <p className="text-slate-500 text-sm mb-2">Need help?</p>
            <div className="flex justify-center space-x-4 text-sm">
              <button className="text-emerald-400 hover:text-emerald-300">📧 Contact Support</button>
              <button className="text-slate-400 hover:text-slate-300">📖 FAQ</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const DetailedTeaserRoadmap = () => (
    <div className="space-y-6">
      <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
        <h3 className="font-bold text-xl text-white mb-4 flex items-center">
          <Calendar className="w-6 h-6 text-emerald-400 mr-2" />
          Your Learning Journey Begins
        </h3>

        <div className="space-y-4">
          <div className="flex items-start border-l-2 border-emerald-400 pl-4">
            <div className="flex-shrink-0 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white text-sm font-bold mr-4">1</div>
            <div>
              <h4 className="font-semibold text-white">Weeks 1-2: Foundation & Assessment</h4>
              <p className="text-slate-400 text-sm mt-1">Complete skills assessment, understand industry landscape, and set learning goals.</p>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="bg-emerald-900/50 text-emerald-300 text-xs px-2 py-1 rounded">Industry Overview</span>
                <span className="bg-emerald-900/50 text-emerald-300 text-xs px-2 py-1 rounded">Skills Gap Analysis</span>
              </div>
            </div>
          </div>

          <div className="flex items-start border-l-2 border-emerald-400 pl-4">
            <div className="flex-shrink-0 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white text-sm font-bold mr-4">2</div>
            <div>
              <h4 className="font-semibold text-white">Weeks 3-6: Core Skills Development</h4>
              <p className="text-slate-400 text-sm mt-1">Master fundamental concepts through hands-on projects and structured learning.</p>
              <div className="flex flex-wrap gap-2 mt-2">
                <span className="bg-blue-900/50 text-blue-300 text-xs px-2 py-1 rounded">Technical Skills</span>
                <span className="bg-blue-900/50 text-blue-300 text-xs px-2 py-1 rounded">Project Portfolio</span>
              </div>
            </div>
          </div>

          <div className="flex items-start border-l-2 border-slate-500 pl-4 opacity-60">
            <div className="flex-shrink-0 w-8 h-8 bg-slate-600 rounded-full flex items-center justify-center text-white text-sm mr-4">
              <Lock className="w-4 h-4" />
            </div>
            <div>
              <h4 className="font-semibold text-slate-300">Months 2-6: Advanced Specialization</h4>
              <p className="text-slate-500 text-sm mt-1">Unlock detailed month-by-month progression, mentorship connections, and certification guidance.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-emerald-900/50 to-teal-900/50 border border-emerald-700/50 rounded-xl p-6">
        <div className="flex items-center mb-4">
          <Lightbulb className="w-6 h-6 text-yellow-400 mr-2" />
          <h3 className="font-bold text-lg text-white">What You'll Get in the Full Version</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          {[
            { icon: MapPin, text: "Month-by-month detailed roadmap" },
            { icon: BookOpen, text: "Curated learning resources & courses" },
            { icon: Users, text: "Industry mentor connections" },
            { icon: Award, text: "Certification recommendations" },
            { icon: BarChart3, text: "Progress tracking tools" },
            { icon: Download, text: "Downloadable PDF roadmap" },
          ].map((item, index) => (
            <div key={index} className="flex items-center">
              <item.icon className="w-4 h-4 text-emerald-400 mr-2 flex-shrink-0" />
              <span className="text-slate-200">{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const RefinementQuestionsModal = () => {
    const allAnswered = refinementQuestions.every(q => refinementAnswers[q.id]);

    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
        <div className="bg-slate-800 rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-white">Personalize Your Roadmap</h2>
            <button
              onClick={() => setShowRefinementQuestions(false)}
              className="text-slate-400 hover:text-white"
            >
              ✕
            </button>
          </div>

          <p className="text-slate-300 mb-8">Help us create the perfect learning path for you by answering these quick questions:</p>

          <div className="space-y-6">
            {refinementQuestions.map((question) => (
              <div key={question.id} className="space-y-3">
                <h3 className="font-semibold text-white">{question.question}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {question.options.map((option) => (
                    <button
                      key={option}
                      onClick={() => handleRefinementAnswer(question.id, option)}
                      className={`p-3 rounded-lg text-left transition-all ${refinementAnswers[question.id] === option
                        ? 'bg-emerald-600 text-white'
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-between items-center mt-8 pt-6 border-t border-slate-700">
            <div className="text-sm text-slate-400">
              {Object.keys(refinementAnswers).length} of {refinementQuestions.length} answered
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowRefinementQuestions(false)}
                className="px-6 py-2 text-slate-400 hover:text-white"
              >
                Skip for now
              </button>
              <button
                onClick={handleProceedToPayment}
                disabled={!allAnswered || isPaymentLoading}
                className={`px-8 py-3 rounded-lg font-semibold flex items-center gap-2 ${allAnswered && !isPaymentLoading
                  ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                  : 'bg-slate-600 text-slate-400 cursor-not-allowed'
                  }`}
              >
                {isPaymentLoading ? (
                  <>
                    <InlineSpinner size="sm" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4" />
                    Upgrade to Starter Plan
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <SEO {...seoConfigs.insights} />
      <div className="min-h-screen bg-slate-900 text-white">
        {showRefinementQuestions && <RefinementQuestionsModal />}

        <div className="max-w-screen-xl mx-auto p-4 sm:p-6 lg:p-8">
          {/* Enhanced Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center space-x-2 bg-emerald-900/50 text-emerald-300 rounded-full px-6 py-3 mb-6">
              <CheckCircle className="w-5 h-5" />
              <span className="text-sm font-medium">Analysis Complete</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-2">Your AI-Powered Career Insights</h1>
            <p className="text-lg text-slate-400 mb-4">We've analyzed your profile and found these top matches for you.</p>
            <div className="flex justify-center items-center space-x-6 text-sm text-slate-500">
              <div className="flex items-center"><Brain className="w-4 h-4 mr-1" /> AI-Powered</div>
              <div className="flex items-center"><Shield className="w-4 h-4 mr-1" /> Personalized</div>
              <div className="flex items-center"><Zap className="w-4 h-4 mr-1" /> Instant Results</div>
            </div>
            <button
              onClick={() => {
                localStorage.removeItem('careerSuggestions');
                window.location.reload();
              }}
              className="mt-4 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg text-sm transition-colors"
            >
              🔄 Refresh Suggestions
            </button>
            {streamProgress && (
              <div className="mt-3 text-sm text-slate-400">
                Progress: {streamProgress.stage?.replace('_', ' ')} ({streamProgress.progress}%)
              </div>
            )}
            {roadmapProgress && (
              <div className="mt-2 text-sm text-slate-400">
                Roadmap: {roadmapProgress.stage?.replace('_', ' ')} ({roadmapProgress.progress}%)
              </div>
            )}
          </div>

          {/* Resume User Personalization Banner */}
          {isResumeUser && (
            <div className="bg-gradient-to-r from-orange-900/80 to-amber-900/80 border border-orange-700/50 rounded-xl p-6 mb-8">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <Sparkles className="w-8 h-8 text-orange-400" />
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="font-bold text-xl text-white mb-2">
                    🎯 Get More Personalized Recommendations
                  </h3>
                  <p className="text-orange-200 mb-4">
                    Your resume analysis is complete! Answer a few quick questions to get a highly personalized roadmap
                    tailored to your timeline, learning style, and career goals.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() => {
                        if (selectedCareer) {
                          handleQuickPersonalize();
                        } else {
                          toast.error("Please select a career path first.");
                        }
                      }}
                      className="bg-orange-600 hover:bg-orange-700 text-white font-semibold px-6 py-3 rounded-lg flex items-center space-x-2 transition-all"
                    >
                      <Target className="w-5 h-5" />
                      <span>Personalize My Roadmap</span>
                    </button>
                    <span className="text-orange-300 text-sm self-center">
                      ⚡ Takes only 2-3 minutes • More targeted recommendations
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Left Column: Enhanced Career List */}
            <aside className="lg:w-1/3 space-y-4">
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 sticky top-8">
                <h2 className="font-bold text-xl mb-4 flex items-center">
                  <Target className="w-5 h-5 text-emerald-400 mr-2" />
                  Top Career Matches
                </h2>
                <div className="space-y-3">
                  {suggestions.map((career, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedCareer(career)}
                      className={`w-full text-left p-4 rounded-lg transition-all border ${selectedCareer?.title === career.title
                        ? 'bg-emerald-600/20 border-emerald-500 text-emerald-300'
                        : 'bg-slate-700/50 border-slate-600 hover:bg-slate-700 hover:border-slate-500'
                        }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-semibold text-lg">{career.title}</span>
                        <span className="text-xs bg-emerald-500 text-white font-bold px-2 py-1 rounded-full">
                          {career.match_percentage}%
                        </span>
                      </div>
                      <p className="text-sm text-slate-400 mb-2">{career.description.substring(0, 80)}...</p>
                      <div className="flex items-center text-xs text-slate-500 space-x-4">
                        <span className="flex items-center"><DollarSign className="w-3 h-3 mr-1" />{career.salary_range}</span>
                        <span className="flex items-center"><TrendingUp className="w-3 h-3 mr-1" />{career.growth_potential}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </aside>

            {/* Right Column: Enhanced Career Details */}
            <main className="lg:w-2/3">
              {selectedCareer ? (
                <div className="space-y-6">
                  {/* Career Header Card */}
                  <div className="bg-gradient-to-r from-slate-800 to-slate-700 border border-slate-600 rounded-xl p-8">
                    <div className="flex justify-between items-start mb-6">
                      <div>
                        <h2 className="text-3xl font-bold text-white mb-2">{selectedCareer.title}</h2>
                        <p className="text-slate-300 text-lg">{selectedCareer.description}</p>
                      </div>
                      <div className="bg-emerald-500 text-white text-lg font-bold px-6 py-3 rounded-full flex-shrink-0 ml-6">
                        {selectedCareer.match_percentage}% Match
                      </div>
                    </div>

                    {/* Enhanced Key Metrics */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                      <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                        <DollarSign className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                        <div className="text-sm text-slate-400">Average Salary</div>
                        <div className="font-bold text-lg text-white">{selectedCareer.salary_range}</div>
                      </div>
                      <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                        <TrendingUp className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                        <div className="text-sm text-slate-400">Growth Rate</div>
                        <div className="font-bold text-lg text-white">{selectedCareer.growth_potential}</div>
                      </div>
                      <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                        <Globe className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                        <div className="text-sm text-slate-400">Work Style</div>
                        <div className="font-bold text-lg text-white">{selectedCareer.location_flexibility}</div>
                      </div>
                      <div className="text-center p-4 bg-slate-800/50 rounded-lg">
                        <Users className="w-8 h-8 text-orange-400 mx-auto mb-2" />
                        <div className="text-sm text-slate-400">Demand</div>
                        <div className="font-bold text-lg text-white">High</div>
                      </div>
                    </div>
                  </div>

                  {/* Why It's a Match */}
                  <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                    <h3 className="font-bold text-xl text-white mb-4 flex items-center">
                      <Star className="w-6 h-6 text-yellow-400 mr-2" />
                      Why This Career Fits You Perfectly
                    </h3>
                    <p className="text-slate-300 leading-relaxed">{selectedCareer.reasoning}</p>
                  </div>

                  {/* Skills Analysis */}
                  <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                    <h3 className="font-bold text-xl text-white mb-4 flex items-center">
                      <BookOpen className="w-6 h-6 text-blue-400 mr-2" />
                      Skills You'll Master
                    </h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {(selectedCareer.required_skills || []).map(skill => (
                        <div key={skill} className="bg-gradient-to-r from-blue-900/50 to-purple-900/50 border border-blue-700/50 text-blue-200 text-sm font-medium px-4 py-2 rounded-lg text-center">
                          {skill}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Company Suggestions (Premium Feature) */}
                  {selectedCareer.company_suggestions && selectedCareer.company_suggestions.length > 0 && (
                    <div className="bg-gradient-to-br from-purple-900/50 to-indigo-900/50 border border-purple-700 rounded-xl p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center">
                          <Building className="w-5 h-5 text-purple-400 mr-2" />
                          Target Companies
                        </h3>
                        <span className="bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                          Premium
                        </span>
                      </div>
                      <p className="text-slate-300 text-sm mb-4">
                        Companies that match your target salary and career goals
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {selectedCareer.company_suggestions.map((company, index) => (
                          <div key={index} className="bg-slate-800/50 border border-slate-600 rounded-lg p-4">
                            <div className="flex justify-between items-start mb-2">
                              <h4 className="font-semibold text-white">{company.name}</h4>
                              <span className="text-xs bg-slate-600 text-slate-300 px-2 py-1 rounded">
                                {company.company_type}
                              </span>
                            </div>
                            <div className="text-sm text-slate-300 mb-3">
                              <div className="flex items-center mb-1">
                                <DollarSign className="w-3 h-3 mr-1 text-green-400" />
                                {company.salary_range_inr}
                              </div>
                            </div>
                            <div className="space-y-2">
                              <div>
                                <h5 className="text-xs font-semibold text-purple-300 mb-1">Culture Highlights:</h5>
                                <ul className="text-xs text-slate-400 space-y-1">
                                  {company.culture_highlights.slice(0, 2).map((highlight, i) => (
                                    <li key={i} className="flex items-start">
                                      <span className="w-1 h-1 bg-purple-400 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                                      {highlight}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div>
                                <h5 className="text-xs font-semibold text-purple-300 mb-1">Application Tips:</h5>
                                <ul className="text-xs text-slate-400 space-y-1">
                                  {company.application_tips.slice(0, 2).map((tip, i) => (
                                    <li key={i} className="flex items-start">
                                      <span className="w-1 h-1 bg-purple-400 rounded-full mt-1.5 mr-2 flex-shrink-0"></span>
                                      {tip}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-4 p-3 bg-purple-800/30 border border-purple-600/50 rounded-lg">
                        <p className="text-sm text-purple-200">
                          💡 <strong>Premium Feature:</strong> Get detailed company insights, application strategies,
                          and salary negotiation tips for each target company.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Detailed Roadmap Preview */}
                  <DetailedTeaserRoadmap />

                  {/* Enhanced CTA Section */}
                  <div className="bg-gradient-to-br from-emerald-900/80 to-teal-900/80 border border-emerald-700 rounded-xl p-8 text-center">
                    <Rocket className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                    <h3 className="font-bold text-2xl text-white mb-2">Ready to Transform Your Career?</h3>
                    <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
                      Get your personalized roadmap with detailed month-by-month guidance, curated resources,
                      and everything you need to succeed in your new career.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
                      <div className="flex items-center text-emerald-300 text-sm">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        30-day money-back guarantee
                      </div>
                      <div className="flex items-center text-emerald-300 text-sm">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Lifetime access to updates
                      </div>
                      <div className="flex items-center text-emerald-300 text-sm">
                        <CheckCircle className="w-4 h-4 mr-2" />
                        PDF download included
                      </div>
                    </div>

                    <button
                      onClick={handleStartRefinement}
                      className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-4 px-8 rounded-xl text-lg transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-3 mx-auto"
                    >
                      <PlayCircle className="w-6 h-6" />
                      <span>Upgrade to Starter Plan</span>
                    </button>

                    <p className="text-slate-400 text-sm mt-4">
                      Monthly subscription • Cancel anytime • Instant access
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-20">
                  <p className="text-slate-400">Select a career path to see the details.</p>
                </div>
              )}
            </main>
          </div>
        </div>
      </div>
    </>
  );
};

export default CareerInsightsPage; 